/**
 * Display helpers for Traffic → Intake Steps, the ordered screen-by-screen funnel
 * served by GET /traffic/intake-steps?mode=screens.
 */

/** 'glp1-history' → 'Glp1 history', 'medical_conditions' → 'Medical conditions'. */
export function screenLabel(step) {
  if (step.kind === 'event') return step.label
  const words = String(step.label || '').replace(/[-_]+/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : '(entry)'
}

/** 7 → '7s', 95 → '1m 35s', 5400 → '1h 30m'. Null stays blank. */
export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—'
  const s = Math.max(0, Math.round(Number(seconds)))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

/** A 0–1 fraction as a percentage, or a dash. */
export function formatPct(fraction, digits = 1) {
  return fraction == null ? '—' : `${(fraction * 100).toFixed(digits)}%`
}

/**
 * The step losing the most people, to highlight. Measured in people rather than
 * rate, so a 50% drop from 4 people to 2 does not outrank losing 800 at BMI.
 * Returns the step_id, or null when nothing drops.
 */
export function biggestLeakStep(steps = []) {
  let worst = null
  let lost = 0
  for (let i = 1; i < steps.length; i++) {
    const drop = steps[i - 1].people - steps[i].people
    if (drop > lost) {
      lost = drop
      worst = steps[i].step_id
    }
  }
  return worst
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** ISO instant → "Sep 29" in ET. */
function etDay(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }).format(date)
}

/**
 * A badge for a screen that launched or was retired inside the range, whose
 * step count includes people who passed it without seeing it (they started
 * outside its lifetime). Null for a screen that existed throughout.
 */
export function screenLifetimeNote(step, availability = {}) {
  const life = step.kind === 'screen' ? availability?.[step.key] : null
  if (!life) return null
  const from = life.from && etDay(life.from)
  const until = life.until && etDay(life.until)
  if (!from && !until) return null
  const badge = from ? `new ${from}` : `removed ${until}`
  const passed = step.passed_through
    ? ` ${step.passed_through.toLocaleString()} people who started ${from ? 'before it launched' : 'after it was removed'} are counted here without seeing it, so the step does not read as a drop-off.`
    : ''
  return { badge, title: `${from ? `First seen ${from}` : ''}${from && until ? '; ' : ''}${until ? `last seen ${until}` : ''}.${passed}` }
}

/** "2026-10-01T14:19:07Z" → "Oct 1, 10:19 AM ET". Null for no value. */
export function formatCurrentThrough(iso) {
  const date = iso ? new Date(iso) : null
  if (!date || Number.isNaN(date.getTime())) return null
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).formatToParts(date).map(part => [part.type, part.value]))
  return `${parts.month} ${parts.day}, ${parts.hour}:${parts.minute} ${parts.dayPeriod} ET`
}

/** { start: '2026-09-04', end: '2026-10-01' } → "Sep 4 – Oct 1". ET day labels, not instants. */
export function formatDayRange(range) {
  if (!range?.start || !range?.end) return null
  const day = value => `${MONTHS[Number(value.slice(5, 7)) - 1]} ${Number(value.slice(8, 10))}`
  return range.start === range.end ? day(range.start) : `${day(range.start)} – ${day(range.end)}`
}
